import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import SearchableSelect from '../SearchableSelect/SearchableSelect';
import { IProductsWithVarietyItem } from '../../../modules/API/Defaults/models/compliteProducts';
import SproutIcon from '../../icons/IconLib/SproutIcon';

interface Props {
    products: IProductsWithVarietyItem[];
    value: IProductsWithVarietyItem | null;
    onSelect: (product: IProductsWithVarietyItem | null) => void;
    placeholder?: string;
    disabled?: boolean;
    readOnly?: boolean;
    className?: string;
}

const compositeKey = (p: IProductsWithVarietyItem) => `${p.ProductID}-${p.VarietyID}`;

const ProductSelector: React.FC<Props> = ({
    products,
    value,
    onSelect,
    placeholder,
    disabled,
    readOnly,
    className,
}) => {
    const [t] = useTranslation('selectors');

    return (
        <SearchableSelect<IProductsWithVarietyItem>
            items={products}
            value={value}
            onSelect={onSelect}
            getKey={(p) => compositeKey(p)}
            getLabel={(p) => `${(p.ProductDesc ?? '').trim()} ${(p.VarietyDesc ?? '').trim()}`.trim()}
            getSearchText={(p) => `${p.ProductDesc ?? ''} ${p.VarietyDesc ?? ''}`}
            renderItem={(p, isSelected) => (
                <Row>
                    <ProductName>{(p.ProductDesc ?? '').trim()}</ProductName>
                    {p.VarietyDesc && (
                        <VarietyName $highlighted={!!p.isHighlighted && !isSelected}>
                            {p.VarietyDesc.trim()}
                        </VarietyName>
                    )}
                </Row>
            )}
            placeholder={placeholder ?? t('selectors_product_placeholder')}
            disabled={disabled}
            readOnly={readOnly}
            leadingIcon={<SproutIcon />}
            className={className}
            emptyLabel={t('selectors_product_empty')}
        />
    );
};

export default ProductSelector;

const Row = styled.span`
    display: inline-flex;
    align-items: baseline;
    gap: 0.5rem;
    min-width: 0;
`;

const ProductName = styled.span`
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 500;
`;

const VarietyName = styled.span<{ $highlighted?: boolean }>`
    color: ${({ theme, $highlighted }) =>
        $highlighted ? theme.colors.accentDeep : theme.colors.ink3};
    font-weight: ${({ $highlighted }) => ($highlighted ? 600 : 400)};
`;
